import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'

import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import ExclamationCircleIcon from '~/components/Icons/svgs/ExclamationCircleIcon'

import { AnswerType, MatchingPairsActivityData, MatchingPairsProps } from './MatchingPairs.type'
import { generateMatchingPairsActivityData, getFeedBackString } from './utils/MatchingPairs.utils'
import MatchingPairsApp from './app/MatchingPairsApp'

const emptyQuestion = {
  question: '',
  questionId: '',
  _key: '',
  pairs: [],
}
function MatchingPairs({
  answerState,
  onNextQuestionClick,
  onAnswerStateChange,
  onCurrentQuestionChange,
  actionButtonsFC,
  questions,
}: MatchingPairsProps): React.ReactElement {
  const [answer, setAnswer] = useState<boolean[]>([])
  const [questionHistory, setQuestionHistory] = useState<number[]>([])
  const [feedback, setFeedback] = useState<AnswerType | null>(null)
  const [displayedPairKeys, setDisplayedPairKeys] = useState<string[] | undefined>(undefined)

  const { t } = useTranslation('bootcamp')

  const [activityData, setActivityData] = useState<MatchingPairsActivityData>(() => ({
    question: emptyQuestion,
    questionIndex: 0,
  }))

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  useEffect(() => {
    setAnswer([])
    const newActivityData = generateMatchingPairsActivityData(questions, questionHistory)
    setActivityData(newActivityData)
    setFeedback(null)
    // Reset with the question so stale keys never pair with a new questionId.
    setDisplayedPairKeys(undefined)
    onAnswerStateChange(null)
  }, [questionHistory, questions, onAnswerStateChange])

  // Keyed on the joined keys string, not the array: the effect above yields fresh objects every run.
  const displayedPairKeysString = displayedPairKeys?.join(' ')
  useEffect(() => {
    if (!activityData.question.questionId) return
    onCurrentQuestionChange?.(activityData.question.questionId, displayedPairKeys ? { displayedPairKeys } : undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityData.question.questionId, displayedPairKeysString, onCurrentQuestionChange])

  const handleOnNextQuestionClick = (): void => {
    setQuestionHistory((prevState) => {
      return [activityData.questionIndex, ...prevState].slice(0, questions.length - 1)
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
  }

  const getFeedBack = (feedbackKey: string | null): React.ReactElement => {
    const message = getFeedBackString(feedbackKey, t)
    if (message.length === 0) return <></>
    return (
      <div className="w-full min-h-9 mb-2">
        {message.length > 0 && (
          <div className="flex items-center p-2 bg-purple text-white text-xs md:text-sm leading-4 font-normal text-center rounded-medium">
            <div className="mr-2">
              <span className="shadow-large">
                <SVGIcon icon={ExclamationCircleIcon} pathClassName="text-white" size={19} />
              </span>
            </div>
            {message}
          </div>
        )}
      </div>
    )
  }

  const showFeedbackMessage = useCallback(
    (responseFeedback: AnswerType | null): void => {
      setFeedback(responseFeedback)

      onAnswerStateChange(responseFeedback === AnswerType.CORRECT, activityData.question.questionId)
    },
    [setFeedback, onAnswerStateChange, activityData.question.questionId],
  )

  return (
    <ActivityWrapper>
      <div className="my-auto w-full justify-center items-center text-purple font-semibold text-md mt-2">
        {getFeedBack(feedback)}
        <MatchingPairsApp
          onDisplayedPairsChange={setDisplayedPairKeys}
          question={activityData.question}
          setFeedBackMessage={showFeedbackMessage}
        />
      </div>

      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer.filter((a) => a).length > 0,
          correctAnswer: answerState ?? false,
          submitAnswerAction: () => {},
          clearFormAction: () => {
            setAnswer([])
            setFeedback(null)
          },
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

export default MatchingPairs
